import React from 'react';
import { useLocation } from 'react-router-dom';

import Select from 'antd/lib/select';

import Loader from '@common/react/components/Core/LoadingProvider/Loader';
import SimpleSearchInput from '@common/react/components/Forms/SimpleSearchInput/SimpleSearchInput';
import { ItemsProvider, useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import { getSearchParamsFromUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import LoadMoreButton from '@common/react/components/Core/ItemsProvider/LoadMoreButton';
import { fromNow } from '@common/react/utils/timeUtils';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';

import LinkWithPrevLocation from '@common/react/components/UI/LinkWithPrevLocation/LinkWithPrevLocation';

import { DoctorPortal } from '@app/objects/DoctorPortal';
import { Review } from '@app/objects/Review';
import { User } from '@app/objects/User';
import Stars from '@app/components/UI/Stars/Stars';
import { Appointment } from '@app/objects/Appointment';
import ReviewModal from '@app/components/UI/ReviewModal/ReviewModal';

import '@app/scss/components/reviews.scss';

interface IProps {
	doctor: DoctorPortal;
	appointment?: Appointment | null;
	hideButton?: boolean;
}

interface ReviewsProps {
	doctor: DoctorPortal;
	reviews?: Array<Review>;
	appointment?: Appointment | null;
	locationId?: number;
	hideButton?: boolean;
}

interface Order {
	title: string;
	value: number;
	column: any;
}

const orders: Array<Order> = [
	{ title: 'Most relevant', value: 0, column: [] },
	{ title: 'Highest rated', value: 1, column: [{ caption: 'rate', direction: 2 }] },
	{ title: 'Lowest rated', value: 2, column: [{ caption: 'rate', direction: 1 }] },
	{ title: 'Newest', value: 3, column: [{ caption: 'time', direction: 2 }] },
	{ title: 'Oldest', value: 4, column: [{ caption: 'time', direction: 1 }] },
];

const { Option } = Select;

const count = 10;

export const OverallRating: React.FC<{ averageReviewRate: number, totalReviewCount: number}> = ({ averageReviewRate, totalReviewCount }) => {
	return <div className="rate text-center">
		<h5 className="mb10">Overall Rating</h5>
		<h4 className="avg-rate">{averageReviewRate}</h4>
		<div>
			<div className="stars">
				<Stars value={averageReviewRate || 0} />
			</div>
			<span className="review-count">
				{totalReviewCount}
				{' '}
				reviews
			</span>
		</div>
	</div>;
};

const ReviewsInner: React.FC<IProps> = ({
	doctor,
	appointment,
	hideButton,
}) => {
	const { getUser } = useApplicationContext();
	const user = getUser<User>();

	const { state: { items } } = useItemsProviderContext<Review>();

	return (
		<div className="review-rate-block">
			<OverallRating averageReviewRate={doctor.averageReviewRate} totalReviewCount={doctor.totalReviewCount} />
			<div className="reviews">
				{items
					? <div className="mb10">
						{items.length > 0
							? <>
								<h3>Recent reviews</h3>
								{items.map((value) =>
									<div className="review" key={value.id}>
										<div className="mb10">
											<p>{value.textEn}</p>
										</div>
										<strong className="mr10">
											{value.author}
											{' '}
										</strong>
										<strong>{fromNow(value.time)}</strong>
									</div>)
								}
							</> : 'No reviews yet'
						}
					</div>
					: <Loader />
				}
				{user && appointment
					? appointment.review
						? (
							<div className="review-button add-review" style={{ textAlign: 'end' }}>
								<ReviewModal review={appointment.review} buttonClassName="btn-primary" />
							</div>
						)
						: (
							<div className="review-button add-review" style={{ textAlign: 'end' }}>
								<LinkWithPrevLocation
									title="Write a review for the doctor"
									className="btn btn-primary"
									to={`/doctor-review/${doctor.portalPathEn}`}
								>
									Leave review
								</LinkWithPrevLocation>
							</div>
						)
					: hideButton ? null : <div className="review-button add-review" style={{ textAlign: 'end' }}>
						<strong>
							To leave a review, you must visit this doctor.
						</strong>
					</div>
				}
			</div>
		</div>
	);
};

const Reviews: React.FC<ReviewsProps> = ({
	doctor,
	locationId,
	reviews,
	appointment,
	hideButton,
}) => {
	return <ItemsProvider<Review>
		items={reviews}
		type="review"
		loadRequest="reviewList"
		unhandledFilters={{
			column: [{ caption: 'time', direction: 2 }],
		}}
		filters={{
			doctorId: locationId ? undefined : doctor.id,
			locationId,
			count: 2,
		}}
		skipInitLoad={!!reviews}
		pagination={{ pageSize: 2, current: 1, total: reviews?.length }}
	>
		<ReviewsInner
			doctor={doctor}
			appointment={appointment}
			hideButton={hideButton}
		/>
	</ItemsProvider>;
};

const AllReviewsInner: React.FC<{doctor: DoctorPortal}> = ({
	doctor,
}) => {
	const { state: { items, filters, pagination }, actions: { handleChange } } = useItemsProviderContext<Review>();

	return (
		<div className="all-reviews scroll-top" id="reviews">
			<div className="reviews-title">
				<div className="mr10">
					<h3>Patient Reviews</h3>
					All reviews have been submitted by patients after seeing the provider.
				</div>
				<div className="rate ml10">
					<h5 className="mb10">Overall Rating</h5>
					<h4 className="avg-rate">
						{doctor.averageReviewRate}
						<span className="stars ml10">
							<Stars value={doctor.averageReviewRate} />
						</span>
					</h4>
				</div>
			</div>
			<div className="reviews-title">
				<h3>
					{filters.text ? pagination.total : doctor.totalReviewCount}
&nbsp;reviews
				</h3>
				<div className="ml10 reviews__search">
					<div className="pull-right mb10">
						<SimpleSearchInput
							defaultValue={filters.text}
							onSubmit={handleChange}
							name="text"
							withoutForm
						/>
					</div>
					<Select
						className="reviews-select pull-right mr10 mb10"
						popupClassName="general-form-select-dropdown"
						getPopupContainer={(node) => node.closest('.all-reviews') || document.body}
						value={filters.column
							? orders.find((order) => order.column[0]?.caption === filters.column[0]?.caption
								&& order.column[0]?.direction === filters.column[0]?.direction)?.value
							: 3
						}
						defaultValue={filters.column
							? orders.find((order) => order.column[0]?.caption === filters.column[0]?.caption
								&& order.column[0]?.direction === filters.column[0]?.direction)?.value
							: 3
						}
						onChange={(value: number) => {
							handleChange({
								column: orders[value].column,
							});
						}}
					>
						{orders.map((order) =>
							<Option value={order.value} key={order.value}>{order.title}</Option>)}
					</Select>
				</div>
			</div>
			{items && <div className="mb20">
				{items.length > 0 ? items.map((value, index) => (
					<div key={value.id} className="review mb10">
						<div className="reviews-title">
							<div className="stars">
								<Stars value={value.rate} />
								<Stars value={5 - Math.round(value.rate)} className="star-disabled" />
							</div>
							<div className="ml10">
								<strong className="mr10 review-author" title={value.author}>
									{' '}
								</strong>
								<strong className="mr10">{fromNow(value.time)}</strong>
							</div>
						</div>
						<p>{value.textEn}</p>
					</div>)) : <h4>no reviews found</h4>}
				<div className="text-center">
					<LoadMoreButton
						className="btn load-more"
					>
						More reviews
					</LoadMoreButton>
				</div>
			</div>}
		</div>
	);
};

export const AllReviews: React.FC<Omit<ReviewsProps, 'user'>> = ({
	doctor,
	reviews,
	locationId,
}) => {
	const location = useLocation();
	const filters = getSearchParamsFromUrl(location) as any;

	return <ItemsProvider<Review>
		items={reviews}
		type="review"
		loadRequest="reviewList"
		filters={{
			...filters,
			doctorId: locationId ? undefined : doctor.id,
			locationId,
			count: 10,
		}}
		skipInitLoad={!!reviews}
		pagination={{ pageSize: filters.pageSize || 10, current: filters.current || 1, total: doctor.totalReviewCount }}
	>
		<AllReviewsInner
			doctor={doctor}
		/>
	</ItemsProvider>;
};

export default Reviews;
